/**
 * Helper hook for getting availability data for a specific date/meal slot
 * Provides memoized availability lookups for efficient component rendering
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '@/lib/api/users';
import type { AvailabilitySummary, MealType, User } from '@/types/api';

export interface AvailabilityForDateResult {
  /** Users who are present for this slot */
  presentUsers: Array<{
    id: number;
    name: string;
    isOrganizer: boolean;
  }>;
  /** Total number of users in the system */
  totalUsers: number;
  /** Count of present users */
  presentCount: number;
  /** Whether data is still loading */
  isLoading: boolean;
}

export interface UseAvailabilityForDateOptions {
  /** Date to filter by (YYYY-MM-DD) */
  date?: string;
  /** Meal type to filter by */
  mealType?: MealType;
  /** All availability summary data (from useAvailabilitySummary) */
  availabilitySummary?: AvailabilitySummary[];
  /** Whether to enable user fetching */
  enabled?: boolean;
}

/**
 * Hook to get availability details for a specific date/meal slot
 *
 * Features:
 * - Filters availability summary by date and meal type
 * - Enriches with user details (name, organizer status)
 * - Memoizes results for performance
 * - Handles loading states
 * - Returns empty state when no match found
 *
 * @example
 * ```tsx
 * function MealSlot({ date, mealType }: { date: string; mealType: MealType }) {
 *   const { data: availabilityData } = useAvailabilitySummary({
 *     startDate: weekStart,
 *     endDate: weekEnd,
 *   });
 *
 *   const availability = useAvailabilityForDate({
 *     date: '2025-10-13',
 *     mealType: 'lunch',
 *     availabilitySummary: availabilityData?.summary,
 *   });
 *
 *   return (
 *     <div>
 *       <p>{availability.presentCount} / {availability.totalUsers} available</p>
 *       {availability.presentUsers.map(user => (
 *         <div key={user.id}>
 *           {user.name} {user.isOrganizer && '(Organizer)'}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailabilityForDate({
  date,
  mealType,
  availabilitySummary,
  enabled = true,
}: UseAvailabilityForDateOptions): AvailabilityForDateResult {
  // Fetch users to enrich availability data
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers(),
    enabled,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Memoize availability calculation
  const result = useMemo<Omit<AvailabilityForDateResult, 'isLoading'>>(() => {
    // Empty state when prerequisites not met
    if (!date || !mealType || !availabilitySummary || !usersData) {
      return {
        presentUsers: [],
        totalUsers: usersData?.total || 0,
        presentCount: 0,
      };
    }

    // Find matching availability slot
    const slot = availabilitySummary.find(
      (item) => item.date === date && item.mealType === mealType
    );

    // No availability data for this slot
    if (!slot) {
      return {
        presentUsers: [],
        totalUsers: usersData.total,
        presentCount: 0,
      };
    }

    // Map present user IDs to user details
    const presentUsers = usersData.users
      .filter((user: User) => slot.presentUsers.some(pu => pu.userId === user.id))
      .map((user: User) => ({
        id: user.id,
        name: user.name,
        isOrganizer: user.isOrganizer,
      }))
      .sort((a, b) => {
        // Sort organizers first, then alphabetically
        if (a.isOrganizer && !b.isOrganizer) return -1;
        if (!a.isOrganizer && b.isOrganizer) return 1;
        return a.name.localeCompare(b.name);
      });

    return {
      presentUsers,
      totalUsers: usersData.total,
      presentCount: presentUsers.length,
    };
  }, [date, mealType, availabilitySummary, usersData]);

  return {
    ...result,
    isLoading: isUsersLoading,
  };
}
